import {
  BookDamagedGlassV1,
  BookDamagedGlassV2,
  BrandFeatureName,
  CarOwnerShipType,
  Claim,
  Configuration,
  DamageImpactV2,
  Fitter,
  FitterOpeningPeriod,
  FitterServiceType,
  FitterServiceTypeSearch,
  GlassImpact,
  OffsetDurations,
  OperationType,
  PhoneNumber,
  SlotsDamagedGlassV1,
  SlotsDamagedGlassV2,
  XOperationType,
} from '../';

import dayjs from './dayjs-utils';
import { getDistanceFromLatLonInKm } from './geocoding';
import { SinisterDataUtils } from './sinister-data-utils';

export function getDamageImpacts(impacts: GlassImpact): DamageImpactV2[] {
  if (!impacts) return [];

  let impact: {
    [key: string]: any;
  } = {};

  if (impacts?.size) {
    impact.size = SinisterDataUtils.getNormalizedDamageSize(impacts.size);
  }
  if (![null, undefined].includes(impacts?.inVisionField)) {
    impact.isInViewField = impacts.inVisionField;
  }
  if (![null, undefined].includes(impacts?.atOuterEdge)) {
    impact.isInEdge = impacts.atOuterEdge;
  }
  return [impact];
}

export function getSlotsDamageV1(
  claim: Claim,
  custodeDiff: boolean
): SlotsDamagedGlassV1 {
  let damage: SlotsDamagedGlassV1 = {
    window: [
      {
        window: SinisterDataUtils.getNormalizedGlass(claim?.glass, custodeDiff),
      },
    ],
    type: SinisterDataUtils.getNormalizedDamagedType(
      claim?.glass?.impacts?.impactsType
    ),
  };
  if (damage.type === 'I') {
    damage.impacts = getDamageImpacts(claim?.glass?.impacts);
  }
  return damage;
}

export function getSlotsDamageV2(
  claim: Claim,
  custodeDiff: boolean
): SlotsDamagedGlassV2 {
  let damage: SlotsDamagedGlassV2 = {
    window: [
      {
        window: SinisterDataUtils.getNormalizedGlass(claim?.glass, custodeDiff),
        isAdas: SinisterDataUtils.getNormalizedCalibration(claim?.calibration),
      },
    ],
    type: SinisterDataUtils.getNormalizedDamagedType(
      claim?.glass?.impacts?.impactsType
    ),
  };
  if (damage.type === 'I') {
    damage.impacts = getDamageImpacts(claim?.glass?.impacts);
  }
  if (damage.window[0].isAdas == null) {
    delete damage.window[0].isAdas;
  }
  return damage;
}

export function getBookDamage(
  claim: Claim,
  params?: any,
  version?: 'V2'
): BookDamagedGlassV2;

export function getBookDamage(
  claim: Claim,
  params?: any,
  version?: 'V1'
): BookDamagedGlassV1;

export function getBookDamage(
  claim: Claim,
  params?: any,
  version: 'V1' | 'V2' = 'V2'
): BookDamagedGlassV1 | BookDamagedGlassV2 {
  let damage;
  if (version === 'V1') {
    damage = getSlotsDamageV1(claim, params?.custodeDiff);
  } else {
    damage = getSlotsDamageV2(claim, params?.custodeDiff);
  }

  if (params?.window?.oem) {
    damage.window[0].oem = params.window.oem;
  }
  if (params?.window?.isAdas) {
    damage.window[0].isAdas = params.window.isAdas;
  }
  if (params?.circumstance) {
    damage.circumstance = params.circumstance;
  }
  if (params?.date) {
    damage.date = params.date; //.split('T')[0].replace(/-/g, '');
  }
  return damage;
}

export function getXOperationType(
  operationType: OperationType
): XOperationType {
  return operationType === 'Repair' ? 'R' : 'NR';
}

export function formatFitterBeforeUpsert(
  fitter: Fitter,
  insurerId: string,
  config: Configuration,
  isOwnFitterBrand = false
): Fitter {
  if (!fitter || !config) return fitter;
  return {
    ...fitter,
    insurerId,
    brand:
      !!fitter?.brand &&
      (hasFitterCanDeclareBrandName(config) ||
        fitter.fitterType.toUpperCase() === 'MOBILE' ||
        (hasFitterCanDeclareOwnBrandName(config) && isOwnFitterBrand))
        ? fitter?.brand
        : config.fitterBrands
            ?.find((brand) => brand?.id == fitter.brandId)
            ?.name?.toUpperCase(),
  };
}

export function hasFitterCanDeclareBrandName(config: Configuration) {
  let hasFeature = false;
  for (let index = 0; index < config?.fitterBrands?.length; index++) {
    if (
      config?.fitterBrands[index]?.features?.includes(
        'can-declare-fitter-brand-name'
      )
    ) {
      hasFeature = true;
      break;
    }
  }
  return hasFeature;
}

export function hasFitterCanDeclareOwnBrandName(config: Configuration) {
  let hasFeature = false;
  for (let index = 0; index < config?.fitterBrands?.length; index++) {
    if (
      config?.fitterBrands[index]?.features?.includes(
        'agenda-can-declare-own-fitter-brand-name'
      )
    ) {
      hasFeature = true;
      break;
    }
  }
  return hasFeature;
}

export function getOwnershipType(ownerType: CarOwnerShipType): number {
  switch (ownerType) {
    case 'private':
      return 1;
    case 'businesslike':
      return 2;
    case 'leasing':
      return 3;
    case 'rental':
      return 4;
    default:
      return 1;
  }
}

export function isNotItinerant(fitter): boolean {
  return fitter?.fitterType !== 'MOBILE';
}

export function getOpeningHours(fitter: Fitter, outsideIntervention: boolean) {
  const periodChoiceList: FitterOpeningPeriod[][] = [];
  if (outsideIntervention) {
    // If appointment is searched outside, never use in center opening hours as fallback
    periodChoiceList.push(
      fitter?.openingHours?.webOutside,
      fitter?.openingHours?.outside
    );
  } else {
    // If appointment is searched in center, never use outside opening hours as fallback
    periodChoiceList.push(
      fitter?.openingHours?.webCenter,
      fitter?.openingHours?.center
    );
  }

  const resultOpeningHours =
    periodChoiceList.find((periodChoice) => periodChoice?.length) || [];

  return resultOpeningHours;
}

export function getInterventionDuration(
  serviceTypeInfos: FitterServiceType,
  inMobility: boolean
) {
  if (serviceTypeInfos) {
    return inMobility
      ? +serviceTypeInfos.interventionDurationMobility ||
          +serviceTypeInfos.interventionDuration
      : +serviceTypeInfos.interventionDuration;
  }
}

export function getInterventionOffset(
  serviceTypeInfos: FitterServiceType,
  inMobility: boolean,
  fitterTimeZone: string
) {
  const isActive = serviceTypeInfos?.weekendOffset?.isActive;
  const now = dayjs().tz(fitterTimeZone);
  const nowWeekday = now.weekday();
  const nowHour = now.format('HH:mm');

  let weekendOffset = 0;

  if (isActive) {
    const { endDay, endHour, offSet, startDay, startHour } =
      serviceTypeInfos.weekendOffset;

    if (startDay < endDay) {
      if (nowWeekday < startDay || endDay < nowWeekday) weekendOffset = offSet;
    } else if (endDay < startDay) {
      if (nowWeekday < endDay || nowWeekday < startDay) weekendOffset = offSet;
    }

    if (!weekendOffset) {
      if (
        (nowWeekday === startDay && nowHour >= startHour) ||
        (nowWeekday === endDay && endHour >= nowHour)
      )
        weekendOffset = offSet;
    }
  }

  const mobilityOffset = inMobility
    ? serviceTypeInfos?.mobilityOffSet
    : undefined;

  return (
    (mobilityOffset ??
      serviceTypeInfos?.offSet ??
      (serviceTypeInfos.operationType === 'Replace' ? 20 : 1)) + weekendOffset
  );
}

export function getUsedInterventionOffset(
  serviceTypeInfos: FitterServiceType,
  inMobility: boolean,
  defaultOffsets?: { center: OffsetDurations; mobility: OffsetDurations }
) {
  let usedOffsetDurations: OffsetDurations;

  if (inMobility) {
    usedOffsetDurations = {
      replace:
        serviceTypeInfos?.mobilityOffSet ??
        serviceTypeInfos?.offSet ??
        defaultOffsets?.mobility?.replace ??
        defaultOffsets?.center?.replace ??
        20,
      repair:
        serviceTypeInfos?.mobilityOffSet ??
        serviceTypeInfos?.offSet ??
        defaultOffsets?.mobility?.repair ??
        defaultOffsets?.center?.repair ??
        1,
    };
  } else {
    usedOffsetDurations = {
      replace:
        serviceTypeInfos?.offSet ?? defaultOffsets?.center?.replace ?? 20,
      repair: serviceTypeInfos?.offSet ?? defaultOffsets?.center?.repair ?? 1,
    };
  }

  return serviceTypeInfos.operationType === 'Replace'
    ? usedOffsetDurations.replace
    : usedOffsetDurations.repair;
}

export function hasBrandFeature(
  feature: BrandFeatureName,
  brandId: string,
  config: Configuration
): boolean {
  const fitterBrand = config.fitterBrands?.find(
    (brand) => brand.id === brandId
  );
  return fitterBrand && fitterBrand.features.indexOf(feature) > -1;
}

function isSameOrNull(testedAttr: unknown, comparedAttr: unknown) {
  return (
    testedAttr === undefined ||
    testedAttr === null ||
    testedAttr === comparedAttr
  );
}

export function findAllSimilarServiceTypesForAppointmentSpecifications(
  serviceTypeInfos: FitterServiceTypeSearch,
  serviceTypeList: FitterServiceType[]
): FitterServiceType[] {
  return serviceTypeList.filter(
    (serviceType) =>
      // uncomment to take calibration into account
      // serviceTypeInfos.glassType === serviceType.glassType &&
      // serviceTypeInfos.operationType === serviceType.operationType &&
      // isSameOrNull(
      //   serviceTypeInfos.calibration ? true : undefined,
      //   serviceType.calibration
      // ) &&
      // isSameOrNull(serviceTypeInfos.default, serviceType.default) &&
      // isSameOrNull(serviceTypeInfos.active, serviceType.active) &&
      // isSameOrNull(
      //   serviceTypeInfos.isBookedOnline ? true : undefined,
      //   serviceType.canBookOnline
      // ) &&
      isSameOrNull(
        serviceTypeInfos.isMobility === false ? true : undefined,
        serviceType.canMakeInCenterIntervention
      ) &&
      isSameOrNull(
        serviceTypeInfos.isMobility ? true : undefined,
        serviceType.canMakeOutsideIntervention
      ) &&
      isSameOrNull(
        serviceTypeInfos.replacementVehicle ? true : undefined,
        serviceType.canShareReplacementVehicle
      )
  );
}

export function findAllSimilarServiceTypesForClaimSpecifications(
  serviceTypeInfos: FitterServiceTypeSearch,
  serviceTypeList: FitterServiceType[]
): FitterServiceType[] {
  return serviceTypeList.filter(
    (serviceType) =>
      serviceTypeInfos.glassType === serviceType.glassType &&
      serviceTypeInfos.operationType === serviceType.operationType &&
      isSameOrNull(serviceTypeInfos.default, serviceType.default) &&
      isSameOrNull(serviceTypeInfos.active, serviceType.active) &&
      isSameOrNull(
        serviceTypeInfos.isBookedOnline ? true : undefined,
        serviceType.canBookOnline
      )
  );
}

export function findCorrespondingServiceType(
  serviceTypeInfos: FitterServiceTypeSearch,
  serviceTypeList: FitterServiceType[]
): FitterServiceType {
  const directIdMatch = serviceTypeList.find(
    (st) => st?.id === serviceTypeInfos?.id
  );
  if (directIdMatch) {
    return directIdMatch;
  }

  // Take all serviceType corresponding to the claim (glassType, operationType) and config (default, active, canBookOnline) specifications
  let allPossibleServiceTypes =
    findAllSimilarServiceTypesForClaimSpecifications(
      serviceTypeInfos,
      serviceTypeList
    );

  // Specify on the appointments demands (outsideIntervention, replacementVehicle)
  allPossibleServiceTypes =
    findAllSimilarServiceTypesForAppointmentSpecifications(
      serviceTypeInfos,
      allPossibleServiceTypes
    );

  //#region Remove this filter when the calibration is taken into account : specify according to calibration if possible
  if (
    serviceTypeInfos?.calibration === true ||
    serviceTypeInfos?.calibration === false
  ) {
    let filter = allPossibleServiceTypes.filter(
      (serviceType) =>
        serviceTypeInfos.calibration === !!serviceType.calibration
    );
    allPossibleServiceTypes =
      filter?.length > 0 ? filter : allPossibleServiceTypes;
  }
  //#endregion

  // if answer not filtered by "default", take the default intervention if possible
  if (serviceTypeInfos.default !== true && serviceTypeInfos.default !== false) {
    let filter = allPossibleServiceTypes.filter(
      (serviceType) => serviceType.default === true
    );
    allPossibleServiceTypes =
      filter?.length > 0 ? filter : allPossibleServiceTypes;
  }

  return allPossibleServiceTypes[0];
}

export function calculateFitterDistance(
  fitter: Fitter,
  lat: number,
  lon: number
) {
  return {
    ...fitter,
    distance:
      fitter?.location?.lat && fitter?.location?.lon
        ? getDistanceFromLatLonInKm(
            lat,
            lon,
            fitter.location.lat,
            fitter.location.lon
          )
        : 0,
  };
}

export function calculateFittersDistance(
  fitters: any,
  lat: number,
  lon: number
) {
  return fitters.map((fitter) => calculateFitterDistance(fitter, lat, lon));
}

export function canInsurerUpdate(authService, configId) {
  return (
    authService.isInsurer() &&
    authService.isInsurerX(configId) &&
    authService.haveAccessPartnerList() &&
    !authService.isFitterB2B() &&
    !authService.isBrand()
  );
}

export function canFitterUpdate(authService, configId, fitter) {
  return (
    authService.isFitter() &&
    authService.isFitterBrandX(configId) &&
    (authService.isFitterSuperAdmin() ||
      ((authService.hasFitterParamAccess() || authService.isFitterAdmin()) &&
        userHasFitterManagerAccessTypeCanUpdate(
          configId,
          authService.accessData.sub,
          fitter
        )))
  );
}

export function canFitterDelete(authService, configId, fitter) {
  return (
    authService.isFitter() &&
    authService.isFitterBrandX(configId) &&
    (authService.isFitterSuperAdmin() || authService.isFitterAdmin()) &&
    userHasFitterManagerAccessTypeCanDelete(
      configId,
      authService.accessData.sub,
      fitter
    )
  );
}

export function userHasFitterManagerAccessTypeCanUpdate(
  configId,
  userId,
  fitter
) {
  return (
    userHasFitterManagerAccess('grpFitter_' + configId, userId, fitter) &&
    (userHasFitterManagerAccess('grpFitterParam', userId, fitter) ||
      userHasFitterManagerAccess('grpFitterSuperAdmin', userId, fitter) ||
      userHasFitterManagerAccess('grpFitterAdmin', userId, fitter))
  );
}

export function userHasFitterManagerAccessTypeCanDelete(
  configId,
  userId,
  fitter
) {
  return (
    userHasFitterManagerAccess('grpFitter_' + configId, userId, fitter) &&
    (userHasFitterManagerAccess('grpFitterSuperAdmin', userId, fitter) ||
      userHasFitterManagerAccess('grpFitterAdmin', userId, fitter))
  );
}

export function userHasFitterManagerAccess(accessType, userId, fitter) {
  return fitter?.managers
    ?.find((manager) => manager?.email === userId)
    ?.accessTypes?.includes(accessType);
}

export function fitterUsesAariAgenda(fitter: Fitter) {
  return !!fitter?.managers?.find(
    (m) => !!m?.email && m?.accessTypes?.length > 0
  );
}

export function checkOverlapHours(period1: any, period2: any): boolean {
  const start1 = dayjs(period1.open.time, 'HHmm');
  const end1 = dayjs(period1.close.time, 'HHmm');
  const start2 = dayjs(period2.open.time, 'HHmm');
  const end2 = dayjs(period2.close.time, 'HHmm');

  return (
    start1.isBetween(start2, end2) ||
    end1.isBetween(start2, end2) ||
    start2.isBetween(start1, end1) ||
    end2.isBetween(start1, end1)
  );
}

// Vérifie si les heures pour un jour donné ne se chevauchent pas
export function checkOverlapDay(
  day: number,
  periods: FitterOpeningPeriod[]
): boolean {
  for (let i = 0; i < periods?.length; i++) {
    for (let j = i + 1; j < periods?.length; j++) {
      if (
        periods[i].open.day === day &&
        periods[i].close.day === day &&
        periods[j].open.day === day &&
        periods[j].close.day === day &&
        checkOverlapHours(periods[i], periods[j])
      ) {
        return false;
      }
    }
  }
  return true;
}

// Vérifie si les heures pour tous les jours ne se chevauchent pas
export function checkOverlapOpeninghours(
  openningPeriod: FitterOpeningPeriod[]
): boolean {
  for (let day = 1; day <= 7; day++) {
    const periods = openningPeriod?.filter(
      (p) => p?.open?.day === day && p?.close?.day === day
    );

    if (periods?.length > 1 && !checkOverlapDay(day, periods)) {
      return true;
    }
  }
  return false;
}

export function removeDuplicateOpeningHours(
  openningPeriod?: FitterOpeningPeriod[]
) {
  const resultOpenings: FitterOpeningPeriod[] = [];

  openningPeriod?.forEach((openning) => {
    const foundExisting = resultOpenings.some(
      (existingOpening) =>
        existingOpening.open.day === openning.open.day &&
        existingOpening.open.time === openning.open.time &&
        existingOpening.close.day === openning.close.day &&
        existingOpening.close.time === openning.close.time
    );

    if (!foundExisting) {
      resultOpenings.push(openning);
    }
  });

  return resultOpenings;
}

export function getActiveEmailListFromContactList(fitter: Fitter): string[] {
  return (
    fitter.contactList
      ?.filter(
        (contact) => contact.activeEmail && contact.email !== fitter.email
      )
      ?.map((contact) => contact.email) || []
  );
}

export function getActivePhoneListFromContactList(
  fitter: Fitter
): PhoneNumber[] {
  return (
    fitter.contactList
      ?.filter(
        (contact) =>
          contact.activePhone && contact.phone.number !== fitter.phone
      )
      ?.map((contact) => contact.phone) || []
  );
}

export function findAllSimilarServiceTypesForCalendarLegend(
  serviceTypeInfos: FitterServiceType,
  serviceTypeList: FitterServiceType[]
): FitterServiceType[] {
  return serviceTypeList.filter(
    (serviceType) =>
      serviceTypeInfos.glassType === serviceType.glassType &&
      serviceTypeInfos.operationType === serviceType.operationType &&
      serviceTypeInfos?.calibration === serviceType.calibration
  );
}
